import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { icons } from './icons.ts'
import { Tooltip } from './Tooltip.tsx'
import { FOCUS_RING } from './focusRing.ts'

/**
 * The controls of the divergence-only pass (#641): the switch beside the
 * Comparação title, and the line that says what it left out.
 */

/**
 * One icon beside the panel's title, not among the header's icons on the
 * right: those open something, this changes what the whole panel shows.
 *
 * Solid while on and outlined while off, so the state reads at a glance; the
 * words are in the tooltip, as on the header's other icons. A label and a
 * second mark beside the title said the same thing twice and crowded it.
 */
export function DivergenceOnlyToggle({
  on,
  onChange,
}: {
  on: boolean
  onChange: (on: boolean) => void
}) {
  return (
    <Tooltip
      text={
        on
          ? 'Mostrando somente as divergências. Clique para ver a conferência completa.'
          : 'Mostrar somente as divergências: uma etapa por divergência, sem os itens que bateram.'
      }
    >
      <button
        type="button"
        aria-pressed={on}
        aria-label="Somente divergências"
        onClick={() => onChange(!on)}
        className={`rounded-base inline-flex size-7 cursor-pointer items-center justify-center border text-xs transition-colors ${FOCUS_RING} ${
          on
            ? 'bg-fill-brand text-text-inverse-strong border-transparent'
            : 'border-stroke-weak text-text-weak bg-fill-white hover:text-text-brand hover:border-stroke-brand-weak'
        }`}
      >
        <FontAwesomeIcon icon={icons.divergenceOnly} aria-hidden="true" />
      </button>
    </Tooltip>
  )
}

/**
 * How many entries the pass left out, and the way back to all of them.
 *
 * With no divergence at all, it says so plainly, and the caller shows the full
 * pass under it rather than an empty step: there is nothing to answer, but the
 * verification still has to be read and concluded somewhere.
 */
export function DivergenceOnlyNotice({
  hidden,
  divergences,
  onShowAll,
}: {
  hidden: number
  divergences: number
  onShowAll: () => void
}) {
  const showAll = (text: string) => (
    <button
      type="button"
      onClick={onShowAll}
      className={`text-tP text-text-brand cursor-pointer border-none bg-transparent p-0 underline-offset-2 hover:underline ${FOCUS_RING}`}
    >
      {text}
    </button>
  )

  if (divergences === 0) {
    return (
      <div className="border-stroke-weak border-b px-5 py-4">
        <div
          role="status"
          className="rounded-base border-stroke-success-weak bg-fill-success-weak flex flex-col gap-1 border p-4"
        >
          <p className="text-tH6 text-text-strong m-0">Nenhuma divergência nesta conferência</p>
          <p className="text-tP text-text-weak m-0">
            Tudo o que foi comparado bateu com a receita. A conferência completa está abaixo para
            você revisar e concluir.
          </p>
          <div>{showAll('Desligar Somente divergências')}</div>
        </div>
      </div>
    )
  }

  return (
    <div className="border-stroke-weak flex flex-wrap items-center gap-2 border-b px-5 py-2">
      <span className="text-tP text-text-weak">
        {hidden === 0
          ? 'Todos os itens têm divergência'
          : hidden === 1
            ? '1 item sem divergência oculto'
            : `${hidden} itens sem divergência ocultos`}
      </span>
      <span aria-hidden="true" className="bg-text-weak inline-block h-1 w-1 rounded-full" />
      {showAll('Ver conferência completa')}
    </div>
  )
}
